<template>
  <li class="alert-bell" :class="{ 'has-unread': unreadCount > 0 }">
    <div class="notification-box">
      <IconButton :title="t('alert.alerts.title')" @click="toggleNotificationPanel" :invert-color="invertColor">
        <svg
          class="notification-icon"
          width="24"
          height="24"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            opacity="0.4"
            d="M10 5.3667V8.1417"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-miterlimit="10"
            stroke-linecap="round"
          />
          <path

            d="M10.0166 1.66663C6.94992 1.66663 4.46658 4.14996 4.46658 7.21663V8.96663C4.46658 9.53329 4.23325 10.3833 3.94158 10.8666L2.88325 12.6333C2.23325 13.725 2.68325 14.9416 3.88325 15.3416C7.86658 16.6666 12.1749 16.6666 16.1582 15.3416C17.2832 14.9666 17.7666 13.65 17.1582 12.6333L16.0999 10.8666C15.8082 10.3833 15.5749 9.52496 15.5749 8.96663V7.21663C15.5666 4.16663 13.0666 1.66663 10.0166 1.66663Z"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-miterlimit="10"
            stroke-linecap="round"
          />
          <path

            d="M12.775 15.6833C12.775 17.2083 11.525 18.4583 9.99998 18.4583C9.24164 18.4583 8.54164 18.1417 8.04164 17.6417C7.54164 17.1417 7.22498 16.4417 7.22498 15.6833"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-miterlimit="10"
          />
        </svg>
      </IconButton>

      <!-- Notification Badge -->
      <span
        v-if="unreadCount > 0"
        class="badge rounded-pill badge-danger animate-pulse"
      >
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
    </div>

    <!-- Notification Panel -->
    <div class="mb-0 alert-dropdown" :class="{ active: isOpen }">
      <!-- Header -->
      <div
        class="card-header bg-white d-flex justify-content-between align-items-center border-bottom px-4 py-3"
      >
        <h6 class="mb-0 fw-semibold fs-6">{{ t('alert.alerts.title') }}</h6>
        <button
          type="button"
          class="btn btn-sm btn-link txt-primary p-0 text-decoration-none"
          @click="viewAllAlerts"
        >
          {{ t('alert.alerts.viewAll') }}
        </button>
      </div>

      <AlertList
        :alerts="unreadAlerts"
        :loading="loading"
        :error="error"
        :unread-count="unreadCount"
        @retry="retryLoad"
        @load-more="loadAlerts"
        @navigate="isOpen = false"
      />
    </div>
  </li>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Trans } from '@/i18n';
import { ETranslationModule } from '@/i18n/translation';
import { websocketService } from '@/services/websocketService';
import IconButton from '@/components/common/IconButton.vue';
import AlertList from './AlertList.vue';

defineOptions({ name: 'AlertBell' });

const props = defineProps({
  invertColor: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const isOpen = ref(false);

// Store getters
const storeAlerts = computed(() => store.getters['alert/alerts']);
const unreadCount = computed(() => store.getters['alert/unreadCount']);
const loading = computed(() => store.getters['alert/isLoading']);
const error = computed(() => store.getters['alert/error']);

// Only show alerts that haven't been marked as read yet
const unreadAlerts = computed(() => (storeAlerts.value || []).filter((a) => !a.userStatus?.isRead));

// Store actions
async function fetchAlerts(payload) {
  return store.dispatch('alert/fetchAlerts', payload);
}

async function fetchUnreadCount() {
  return store.dispatch('alert/fetchUnreadCount');
}

function clearError() {
  store.dispatch('alert/clearError');
}

// Lifecycle
onMounted(async () => {
  Trans.loadModuleTranslations(ETranslationModule.alert).catch((err) =>
    console.error('Failed to load alert translations:', err),
  );
  await initializeAlertSystem();
  setupEventListeners();
});

onBeforeUnmount(() => {
  cleanup();
});

/**
 * Bootstrap the alert system:
 *   1. Fetch unread count via HTTP (populates the badge)
 *   2. Connect to WebSocket (will call fetchUnreadCount on 'new-alert' events)
 */
async function initializeAlertSystem() {
  try {
    await fetchUnreadCount();
    websocketService.initialize(store);
  } catch (err) {
    console.error('Failed to initialise alert system:', err);
  }
}

/**
 * Open/close the dropdown.
 * Always fetches the latest 10 unread alerts when opening.
 */
function toggleNotificationPanel() {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    loadAlerts();
  }
}

async function loadAlerts() {
  try {
    await fetchAlerts({ limit: 10 });
  } catch (err) {
    console.error('Failed to load alerts:', err);
  }
}

async function retryLoad() {
  clearError();
  await loadAlerts();
}

function viewAllAlerts() {
  isOpen.value = false;

  if (route.path === '/alerts') {
    router.push('/').then(() => {
      router.push('/alerts');
    });
  } else {
    router.push('/alerts');
  }
}

function setupEventListeners() {
  document.addEventListener('click', handleOutsideClick);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('storage', handleStorageEvent);
}

function handleOutsideClick(event) {
  if (isOpen.value && !event.target.closest('.alert-bell')) {
    isOpen.value = false;
  }
}

function handleVisibilityChange() {
  if (!document.hidden && !websocketService.getConnectionStatus()) {
    websocketService.connect();
  }
}

function handleStorageEvent(event) {
  if (event.key === 'alert-refresh-trigger' && event.newValue) {
    try {
      const data = JSON.parse(event.newValue);
      if (route.path === '/alerts' && data.type === 'ALERT_MARKED_READ') {
        window.location.reload();
      }
    } catch (err) {
      console.error('Error parsing storage event:', err);
    }
  }
}

function cleanup() {
  document.removeEventListener('click', handleOutsideClick);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('storage', handleStorageEvent);
  websocketService.disconnect();
}

</script>

<style scoped>
.alert-bell {
  position: relative;
}

.notification-box {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.alert-dropdown {
  position: absolute;
  top: 40px;
  right: 0;
  width: 450px !important;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-width: 95vw;
  max-height: 90vh;
  overflow: hidden;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 20px 24px;
  background-color: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  border-radius: 8px 8px 0 0;
}

.dropdown-title {
  font-weight: 600;
  font-size: 18px;
  color: var(--body-font-color);
  margin: 0;
}

.notification-footer {
  padding: 10px 20px;
  background-color: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.view-all-btn {
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: all 0.2s ease;
}

.view-all-btn:hover {
  color: var(--theme-default);
  text-decoration: underline;
}

.animate-pulse {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

/* Badge */
.badge {
  position: absolute;
  top: -8px;
  right: -8px;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
}

.badge-danger {
  background-color: var(--bs-danger);
}

.badge-warning {
  background-color: var(--bs-warning);
  color: var(--body-font-color);
}

/* Show/hide panel */
.alert-dropdown.active {
  display: block !important;
  opacity: 1 !important;
  visibility: visible !important;
  z-index: 9999 !important;
}

.alert-dropdown:not(.active) {
  display: none !important;
  opacity: 0 !important;
  visibility: hidden !important;
}

/* Responsive */
@media (max-width: 768px) {
  .alert-dropdown {
    width: 400px !important;
    max-height: 80vh;
    right: -20px;
  }

  .notification-header {
    padding: 20px 20px 16px 20px;
  }
}

@media (max-width: 480px) {
  .alert-dropdown {
    width: 350px !important;
    max-height: 75vh;
    right: -40px;
  }

  .notification-header {
    padding: 16px 16px 12px 16px;
  }

  .header-controls {
    gap: 8px;
  }
}
</style>
